import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import ReactTooltip from 'react-tooltip';

import { INTERACTIVE_TOOLTIP_HIDE_DELAY_MS } from 'constants/tooltip';
import { localeToLanguageKey } from 'utils/timetableServices';
import { getLiveTrainsURL } from 'utils/urls';

import { StyledDelaySentence, StyledFindOutMoreLink, StyledNews, StyledNewsTitle, StyledTooltipContent } from './TrainDisruption.styles';

const TrainDisruptionTooltip = ({ id, disruptions, origin, destination, startDate, intl: { locale } }) => {
  const languageKey = localeToLanguageKey({ locale });

  const hasText = ({ title, description, commercial }) =>
    !!(title?.[languageKey] || description?.[languageKey] || commercial?.[languageKey]);

  const renderContent = (trainNumber) => {
    const disruption = disruptions.get(trainNumber);

    if (!disruption) {
      return null;
    }

    const { delayMinutes, news } = disruption;
    const liveTrainsUrl = origin && destination && startDate ? getLiveTrainsURL(origin, destination, startDate, trainNumber) : null;

    return (
      <StyledTooltipContent>
        {delayMinutes !== null && (
          <StyledDelaySentence>
            <FormattedMessage id="searchResults.trainDisruptionDelay" values={{ delay: delayMinutes }} />
          </StyledDelaySentence>
        )}
        {news.filter(hasText).map(({ id: newsId, title, description, commercial }) => (
          <StyledNews key={newsId}>
            {!!title?.[languageKey] && <StyledNewsTitle>{title[languageKey]}</StyledNewsTitle>}
            {!!description?.[languageKey] && <p>{description[languageKey]}</p>}
            {!!commercial?.[languageKey] && <p>{commercial[languageKey]}</p>}
          </StyledNews>
        ))}
        {!!liveTrainsUrl && (
          <StyledFindOutMoreLink href={liveTrainsUrl} target="_blank" rel="noreferrer">
            <FormattedMessage id="searchResults.trainDisruptionFindOutMore" />
          </StyledFindOutMoreLink>
        )}
      </StyledTooltipContent>
    );
  };

  return (
    <ReactTooltip
      id={id}
      effect="solid"
      place="right"
      clickable
      delayHide={INTERACTIVE_TOOLTIP_HIDE_DELAY_MS}
      wrapper="span"
      getContent={renderContent}
    />
  );
};

TrainDisruptionTooltip.propTypes = {
  id: PropTypes.string,
  disruptions: PropTypes.instanceOf(Map),
  origin: PropTypes.string,
  destination: PropTypes.string,
  startDate: PropTypes.string,
  intl: intlShape
};

export default injectIntl(TrainDisruptionTooltip);
